// Pure helper that converts an `EngagementVisualState` (the hook's output)
// into the per-unit fields the batched voxel renderer needs. Keeps the
// px→world conversion in one place so the renderer's `useFrame` loop can
// stay focused on matrix writes.
//
// Spec: docs/02-systems/combat-visualisation.md §3 (layers 1+2).

import type { EngagementVisualState, StepDirection } from './useCombatEngagements.js';

export interface RenderableEngagement {
  /** Tick at which jitter stops being applied. 0 = never jitter. */
  readonly jitterUntilTick: number;
  /** Signed world-unit offset relative to the unit's base position. */
  readonly stepOffsetWorld: number;
  /** Direction of the most recent step (or null). */
  readonly stepDirection: StepDirection;
}

export interface RenderableEngagementOptions {
  /** Pixels per world unit — controls how many px of motion one world unit represents. */
  readonly pxPerWorld: number;
}

export const DEFAULT_RENDERABLE_OPTIONS: RenderableEngagementOptions = {
  pxPerWorld: 0.025,
};

const ZERO: RenderableEngagement = {
  jitterUntilTick: 0,
  stepOffsetWorld: 0,
  stepDirection: null,
};

export function toRenderableEngagement(
  state: EngagementVisualState | undefined,
  options: RenderableEngagementOptions = DEFAULT_RENDERABLE_OPTIONS,
): RenderableEngagement {
  if (!state) return ZERO;
  return {
    jitterUntilTick: state.jitterUntilTick,
    stepOffsetWorld: state.stepOffsetPx * options.pxPerWorld,
    stepDirection: state.stepDirection,
  };
}
